import { Injectable, OnDestroy, WritableSignal, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import {
  INotification,
  NotificationSystemService,
} from 'src/app/common-components/services/common-notification-system.service';

import {
  ProviderDirectoryFunctionService,
  ReviewRequest,
} from '../providerdirectoryapi/providerdirectoryfunctionapi.services';
import { UserService } from '../user/user.service';

@Injectable({
  providedIn: 'root',
})
export class AdminApprovalService implements OnDestroy {
  private _response: WritableSignal<boolean> = signal(false);
  private _parameters: WritableSignal<string> = signal('');
  private _requests: WritableSignal<ReviewRequest[]> = signal([]);
  private _reviewed: WritableSignal<boolean> = signal(false);
  private _load: WritableSignal<boolean> = signal(false);
  private _statusPic: WritableSignal<ReviewRequest | undefined> = signal(undefined);
  private _statusAboutMe: WritableSignal<ReviewRequest | undefined> = signal(undefined);
  private _statusSnippet: WritableSignal<ReviewRequest | undefined> = signal(undefined);
  public _statusSelections: WritableSignal<{
    photo: boolean | null;
    about: boolean | null;
    snippet: boolean | null;
    all: boolean | null;
  }> = signal({
    photo: null,
    about: null,
    snippet: null,
    all: null,
  });

  private readonly _destroying$ = new Subject<void>();

  constructor(
    private providerDirectoryFunctionService: ProviderDirectoryFunctionService,
    private notificationSystemService: NotificationSystemService
  ) {}

  public getReviewRequest(data?: string): void {
    if (data !== undefined) {
      this._parameters.set(data);
    } else {
      data = this._parameters();
    }
    this._load.set(true);
    this.providerDirectoryFunctionService
      .getReviewRequestByUserId(data)
      .pipe(take(1), takeUntil(this._destroying$))
      .subscribe({
        next: res => {
          if (res) {
            if (!res.isSuccess) {
              const notification: INotification = { type: 'error', content: res.message ?? '' };
              this.notificationSystemService.setNotification(notification);
            } else {
              if (res.data !== undefined) {
                this._requests.set(res.data);
                this._requests().forEach(x => {
                  switch (x.type) {
                    case 0:
                      this._statusPic.set(x);
                      this._statusSelections.set({
                        ...this._statusSelections(),
                        photo: x.status === 1,
                      });
                      break;
                    case 1:
                      break;
                    case 2:
                      this._statusAboutMe.set(x);
                      this._statusSelections.set({
                        ...this._statusSelections(),
                        about: x.status === 1,
                      });
                      break;
                    case 3:
                      this._statusSnippet.set(x);
                      this._statusSelections.set({
                        ...this._statusSelections(),
                        snippet: x.status === 1,
                      });
                      break;
                  }
                });
              }
            }
          }
        },
        error: error => {
          if (error.message) {
            const notification: INotification = { type: 'error', content: error.message ?? '' };
            this.notificationSystemService.setNotification(notification);
          }
        },
        complete: () => {
          this._load.set(false);
        },
      });
  }

  public createReviewRequest(data?: ReviewRequest[]): void {
    if (data !== undefined) {
      this._requests.set(data);
    } else {
      data = this._requests();
    }
    data = data.filter(item => item !== null);

    // Add encoding for metadata property for each review request
    data?.forEach(request => {
      if (request.metadata != null) {
        request.metadata = btoa(request.metadata);
      }
    });

    this._load.set(true);
    this.providerDirectoryFunctionService
      .createReviewRequest(data)
      .pipe(take(1), takeUntil(this._destroying$))
      .subscribe({
        next: res => {
          if (res) {
            this._response.set(res);
          }
        },
        error: error => {
          if (error.message) {
            const notification: INotification = { type: 'error', content: error.message };
            this.notificationSystemService.setNotification(notification);
          }
        },
        complete: () => {
          this._load.set(false);
        },
      });
  }

  public updateReviewRequest(): void {
    const data = this._requests();
    data.filter(item => item !== null);
    // Add encoding for metadata property for each review request
    data?.forEach(request => {
      if (request.metadata != null) {
        request.metadata = btoa(request.metadata);
      }
    });

    this._load.set(true);
    this.providerDirectoryFunctionService
      .updateReviewRequest(data)
      .pipe(take(1), takeUntil(this._destroying$))
      .subscribe({
        next: res => {
          if (res) {
            this._response.set(res);
          }
        },
        error: error => {
          if (error.message) {
            const notification: INotification = { type: 'error', content: error.message };
            this.notificationSystemService.setNotification(notification);
          }
        },
        complete: () => {
          this._load.set(false);
        },
      });
  }

  public getStatusPicSignal() {
    return this._statusPic;
  }

  public setStatusPicSignal(data: ReviewRequest) {
    this._statusPic.set(data);
  }

  public getStatusAboutMeSignal() {
    return this._statusAboutMe;
  }

  public getStatusSnippetSignal() {
    return this._statusSnippet;
  }

  public getStatusSelectionsSignal() {
    return this._statusSelections;
  }

  public getParametersSignal() {
    return this._parameters();
  }

  public setParameters(data: string) {
    this._parameters.set(data);
  }

  public getRequestsByID(id: string | undefined, override = false) {
    if (override === true && id) {
      this.getReviewRequest(id);
    }
    return this._requests;
  }

  public setRequests(data: ReviewRequest[]) {
    this._requests.set(data);
  }

  public updateRequest(data: ReviewRequest) {
    const current = this._requests();
    if (Array.isArray(current) && current !== null) {
      this._requests.set([...current, data]);
    } else {
      this._requests.set([data]);
    }
  }

  public getResponse() {
    return this._response;
  }

  public getLoad() {
    return this._load;
  }

  public getReviewedSignal() {
    return this._reviewed;
  }

  public setReviewed(data: boolean) {
    this._reviewed.set(data);
  }

  updateStatusPicSignal<K extends keyof ReviewRequest>(key: K, value: ReviewRequest[K]): void {
    if (this._statusPic) {
      const updated = new ReviewRequest({ ...this._statusPic(), [key]: value });
      this._statusPic.set(updated);
    }
  }

  public clear(): void {
    this._response.set(false);
    this._requests.set([]);
    this._parameters.set('');
    this._statusPic.set(undefined);
    this._statusAboutMe.set(undefined);
    this._statusSnippet.set(undefined);
    this._statusSelections.set({
      photo: null,
      about: null,
      snippet: null,
      all: null,
    });
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }
}
